import { isDefined, setAtIndex } from '@eih-netbroker/common';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import classes from './CodeInput.module.scss';
import { CodeInputField } from './CodeInputField';
import { CodeInputEvents } from './events';
import { CodeInputUtils } from './utils';

export type CodeInputHandle = {
  focus: (index?: number) => void;
  blur: () => void;
};

export type CodeInputProps = {
  ref?: React.Ref<CodeInputHandle>;
  secureTextEntry?: boolean;
  value: (string | undefined)[];
  codeLength?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  onChange: (chars: (string | undefined)[]) => void;
  onSubmit: () => void;
};

export const CodeInput = ({
  value,
  secureTextEntry = true,
  codeLength = 4,
  autoFocus = false,
  ref,
  disabled,
  onChange,
  onSubmit,
}: CodeInputProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(autoFocus ? 0 : undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const blur = useCallback((): void => {
    if (isDefined(containerRef.current)) {
      CodeInputUtils.getActiveElement(containerRef.current)?.blur?.();
    }
  }, []);

  const focus = useCallback((index?: number): void => {
    if (!isDefined(containerRef.current)) {
      return;
    }
    // Called with a specific index to focus, do just that.
    if (isDefined(index)) {
      CodeInputUtils.getFieldElementByIndex(containerRef.current, index)?.focus();
      return;
    }

    // Called (possibly from outside) requesting focus on the control.
    // If any fields have focus already do nothing, otherwise focus the first field.
    const active = CodeInputUtils.getActiveElement(containerRef.current);
    if (!isDefined(active)) {
      CodeInputUtils.getFieldElementByIndex(containerRef.current, 0)?.focus();
    }
  }, []);

  useImperativeHandle(ref, (): CodeInputHandle => ({ focus, blur }), [focus, blur]);

  useEffect(() => {
    if (isDefined(selectedIndex)) {
      focus(selectedIndex);
    }
  }, [focus, selectedIndex]);

  const handleFocus = useCallback((event: CodeInputEvents.Focus) => {
    setSelectedIndex(event.index);
  }, []);

  const handleEdit = useCallback(
    (e: CodeInputEvents.Edit) => {
      switch (e.key) {
        case 'Backspace': {
          onChange(setAtIndex(value, e.index, undefined));
          focus(e.index - 1);
          break;
        }
        case 'Delete': {
          onChange(setAtIndex(value, e.index, undefined));
          break;
        }
        case 'ArrowLeft': {
          focus(Math.max(e.index - 1, 0));
          break;
        }
        case 'ArrowRight': {
          focus(Math.min(e.index + 1, codeLength - 1));
          break;
        }
        case 'Enter': {
          if (value.length === codeLength && value.every(isDefined)) {
            onSubmit();
          }
          break;
        }
      }
    },
    [codeLength, focus, onChange, onSubmit, value],
  );

  const handleInput = useCallback(
    (e: CodeInputEvents.Input) => {
      onChange(setAtIndex(value, e.index, e.char));
      focus(Math.min(e.index + 1, codeLength - 1));
    },
    [codeLength, focus, onChange, value],
  );

  const handlePaste = useCallback(
    (event: CodeInputEvents.Paste) => {
      onChange(event.value.split(''));
      focus(codeLength - 1);
    },
    [codeLength, focus, onChange],
  );

  return (
    <div
      className={classes.codeInput}
      ref={containerRef}
    >
      {Array.from({ length: codeLength }, (_, index) => (
        <CodeInputField
          index={index}
          secureTextEntry={secureTextEntry}
          isSelected={selectedIndex === index}
          text={value[index]}
          key={index}
          codeLength={codeLength}
          disabled={disabled}
          onFocus={handleFocus}
          onEdit={handleEdit}
          onInput={handleInput}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
};
